import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  History,
  DollarSign
} from "lucide-react";
import * as ownerApi from "../../api/payroll/salaryStructureApi";
import * as portalApi from "../../api/payroll/salaryStructurePortalApi";
import {
  Button,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function SalaryStructureList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const basePath = isPortal ? "/staff-panel/salary-structures" : "/salary-structures";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getAllSalaryStructures();
        setItems(Array.isArray(res) ? res : res.data || []);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [isPortal, api]);

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = items.map((item) => ({ ...item, id: item._id }));

  const columns = [
    {
      key: "staff",
      header: "Staff",
      render: (item) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">{item.staffId?.name || "—"}</span>
      ),
    },
    {
      key: "code",
      header: "Code",
      render: (item) => (
        <span className="font-mono text-xs text-neutral-500 dark:text-neutral-400">
          {item.staffId?.staffCode || "—"}
        </span>
      ),
    },
    {
      key: "effectiveFrom",
      header: "Effective From",
      render: (item) =>
        item.effectiveFrom ? new Date(item.effectiveFrom).toLocaleDateString() : "—",
    },
    {
      key: "grossMonthly",
      header: "Gross Monthly",
      align: "right",
      render: (item) => (
        <span className="font-mono font-semibold text-neutral-900 dark:text-neutral-100">
          ₹{Number(item.grossMonthly || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "components",
      header: "Components",
      render: (item) => <Badge variant="default">{(item.components || []).length}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (item) => (
        <Link to={`${basePath}/staff/${item.staffId?._id || item.staffId}`}>
          <Button type="button" variant="ghost" size="xs" leftIcon={<History className="h-3.5 w-3.5" />}>
            History
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
            Salary Structures
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            View and manage effective salary structures across staff
          </p>
        </div>

        <Link to={`${basePath}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            New Structure
          </Button>
        </Link>
      </div>

      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={DollarSign}
                title="No salary structures yet"
                description="Create a structure to define a staff member's pay."
                action={
                  <Link to={`${basePath}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      New Structure
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}